import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../../api/axios";
import { MEDICATION_PATHS } from "../../../api/endpoints";

// GET all
export const fetchMedications = createAsyncThunk(
  "medication/fetchMedications",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(MEDICATION_PATHS.ALL(userId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch medications");
    }
  }
);

// GET today / active
export const fetchActiveMedications = createAsyncThunk(
  "medication/fetchActiveMedications",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(MEDICATION_PATHS.TODAY(userId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch active medications");
    }
  }
);

// GET archived
export const fetchArchivedMedications = createAsyncThunk(
  "medication/fetchArchivedMedications",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(MEDICATION_PATHS.ARCHIVED(userId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch archived medications");
    }
  }
);

// CREATE
// export const addMedication = createAsyncThunk(
//   "medication/addMedication",
//   async ({ userId, medicationData, payload }, { rejectWithValue }) => {
//     try {
//       const body = payload || medicationData;

//       const response = await axios.post(
//         MEDICATION_PATHS.CREATE(userId),
//         body
//       );

//       return response.data;
//     } catch (error) {
//       return rejectWithValue(error.response?.data?.message || "Failed to add medication");
//     }
//   }
// );

// export const createMedication = addMedication;

// medicationThunks.js
export const addMedication = createAsyncThunk(
  "medication/addMedication",
  async ({ userId, medicationData, payload }, { rejectWithValue }) => {
    try {
      const body = payload || medicationData;

      // Construire un FormData
      const form = new FormData();

      // Champs scalaires simples
      const scalarFields = ['name', 'alarmOn', 'startDate', 'endDate', 'dosage'];
      for (const key of scalarFields) {
        if (body[key] != null && String(body[key]).trim() !== '') {
          form.append(key, String(body[key]));
        }
      }

      // ✅ quantity (texte ex: "500mg")
      if (body.quantity != null && String(body.quantity).trim() !== '') {
        form.append('quantity', String(body.quantity).trim());
      }

      // ✅ medicationQuantity (nombre)
      if (body.medicationQuantity != null) {
        form.append('medicationQuantity', String(Number(body.medicationQuantity) || 0));
      }

      // times (tableau)
      const times = Array.isArray(body.times) ? body.times : [];
      for (const t of times) {
        form.append('times', t);
      }

      // amount
      form.append('amount', String(body.amount || times.length || 1));

      // tracking
      if (body.tracking) {
        form.append('tracking', JSON.stringify(body.tracking));
      }

      // image (File ou Blob)
      if (body.image instanceof File || body.image instanceof Blob) {
        form.append('image', body.image);
      }

      const response = await axios.post(
        MEDICATION_PATHS.CREATE(userId),
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add medication");
    }
  }
);
export const createMedication = addMedication;

// UPDATE
// export const updateMedication = createAsyncThunk(
//   "medication/updateMedication",
//   async ({ userId, id, payload, medicationData }, { rejectWithValue }) => {
//     try {
//       const body = payload || medicationData;

//       const response = await axios.patch(
//         MEDICATION_PATHS.UPDATE(userId, id),
//         body
//       );

//       return response.data;
//     } catch (error) {
//       return rejectWithValue(error.response?.data?.message || "Failed to update medication");
//     }
//   }
// );

export const updateMedication = createAsyncThunk(
  "medication/updateMedication",
  async ({ userId, id, medicationData, payload, version = null }, { rejectWithValue }) => {
    try {
      const body = payload || medicationData;
      if (!body) throw new Error("Medication data is required");

      const form = new FormData();

      const scalarFields = ['name', 'alarmOn', 'startDate', 'endDate', 'dosage'];
      for (const key of scalarFields) {
        if (body[key] != null && String(body[key]).trim() !== '') {
          form.append(key, String(body[key]));
        }
      }

      // ✅ quantity
      if (body.quantity != null && String(body.quantity).trim() !== '') {
        form.append('quantity', String(body.quantity).trim());
      }

      // ✅ medicationQuantity
      if (body.medicationQuantity != null) {
        form.append('medicationQuantity', String(Number(body.medicationQuantity) || 0));
      }

      const times = Array.isArray(body.times) ? body.times : [];
      for (const t of times) {
        form.append('times', t);
      }

      if (times.length > 0) {
        form.append('amount', String(times.length));
      } else if (body.amount != null) {
        form.append('amount', String(body.amount));
      }

      if (body.tracking) {
        form.append('tracking', JSON.stringify(body.tracking));
      }

      if (body.image instanceof File || body.image instanceof Blob) {
        form.append('image', body.image);
      }

      const response = await axios.patch(
        MEDICATION_PATHS.UPDATE(userId, id),
        form,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(version != null ? { 'X-Resource-Version': String(version) } : {}),
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update medication");
    }
  }
);

// TAKE DOSE
export const takeDose = createAsyncThunk(
  "medication/takeDose",
  async ({ userId, id, index }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        MEDICATION_PATHS.TAKE(userId, id, index),
        {}
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to mark dose as taken");
    }
  }
);

// TOGGLE ALARM
export const toggleAlarm = createAsyncThunk(
  "medication/toggleAlarm",
  async ({ userId, id }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        MEDICATION_PATHS.TOGGLE(userId, id),
        {}
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to toggle alarm");
    }
  }
);

// ARCHIVE
export const archiveMedication = createAsyncThunk(
  "medication/archiveMedication",
  async ({ userId, id, version = null }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        MEDICATION_PATHS.ARCHIVE(userId, id),
        {},
        {
          headers:
            version != null ? { 'X-Resource-Version': String(version) } : undefined,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to archive medication");
    }
  }
);

// DELETE
export const deleteMedication = createAsyncThunk(
  "medication/deleteMedication",
  async ({ userId, id }, { rejectWithValue }) => {
    try {
      await axios.delete(MEDICATION_PATHS.DELETE(userId, id));
      return { id, userId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete medication");
    }
  }
);

// CHECK ARCHIVE
export const checkArchive = createAsyncThunk(
  "medication/checkArchive",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        MEDICATION_PATHS.CHECK_ARCHIVE(userId),
        {}
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to check archive");
    }
  }
);

// HISTORY
export const fetchMedicationHistory = createAsyncThunk(
  "medication/fetchMedicationHistory",
  async ({ userId, id }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        MEDICATION_PATHS.HISTORY(userId, id)
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch medication history");
    }
  }
);

// STATS
export const fetchMedicationStats = createAsyncThunk(
  "medication/fetchMedicationStats",
  async ({ userId, id }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        MEDICATION_PATHS.STATS(userId, id)
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch medication stats");
    }
  }
);

// RESET DOSES
export const resetDoses = createAsyncThunk(
  "medication/resetDoses",
  async ({ userId, id }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        MEDICATION_PATHS.RESET_DOSES(userId, id),
        {}
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to reset doses");
    }
  }
);

// RESET ALL DOSES
export const resetAllDoses = createAsyncThunk(
  "medication/resetAllDoses",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        MEDICATION_PATHS.RESET_ALL_DOSES(userId),
        {}
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to reset all doses");
    }
  }
);
